import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

interface ChunkClassificationDetail {
  chunkId: string;
  chunkContent: string;
  chunkPosition: number;
  taxonomyCategory: string;
  confidence: number;
  method: string;
  classifiedAt: Date;
  contentFingerprint: string;
}

interface ArticleInfo {
  id: string;
  title: string;
  projectId: string | null;
  updatedAt: Date;
}

interface AggregatedResult {
  primaryCategory: string;
  primaryConfidence: number;
  allCategories: Array<{
    category: string;
    weight: number;
    chunkCount: number;
    avgConfidence: number;
  }>;
}

interface ArticleClassificationDetail {
  article: ArticleInfo;
  aggregatedResult: AggregatedResult | null;
  chunkClassifications: ChunkClassificationDetail[];
  lowConfidenceChunks: ChunkClassificationDetail[];
  totalChunks: number;
  classifiedChunks: number;
  strategyInfo: {
    strategy: string;
    confidenceThresholds: {
      llm: number;
      centroid: number;
      aggregation: number;
    };
    chunkType: 'container' | 'content';
    usesEmbeddings: boolean;
  };
}

@Component({
  selector: 'app-article-taxonomy-analysis',
  templateUrl: './article-taxonomy-analysis.component.html',
  styleUrls: ['./article-taxonomy-analysis.component.scss'],
})
export class ArticleTaxonomyAnalysisComponent implements OnInit {
  articleId: string | null = null;
  tenantId: string | null = null;
  dimensionId: string | null = null;
  analysisData: ArticleClassificationDetail | null = null;
  isLoading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.articleId = params.get('articleId');
      this.tenantId = params.get('tenantId');
      this.dimensionId = params.get('dimensionId'); // Optional for backward compatibility
      if (this.articleId && this.tenantId) {
        this.loadAnalysisData();
      } else {
        this.error = 'Missing article ID or tenant ID';
        this.isLoading = false;
      }
    });
  }

  loadAnalysisData() {
    if (!this.articleId || !this.tenantId) return;

    // Dimension ID is required for the multi-dimensional taxonomy system
    if (!this.dimensionId) {
      this.isLoading = false;
      this.error = 'Dimension ID is required. Please access this article from a specific dimension context.';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.analysisData = null;

    const apiUrl = `/knowledge-base/taxonomy/${this.tenantId}/article/${this.articleId}/details?dimensionId=${this.dimensionId}`;

    this.apiService.get<ArticleClassificationDetail>(apiUrl).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.analysisData = response;
      },
      error: (err) => {
        this.isLoading = false;
        this.error = 'Failed to load article analysis: ' + (err.message || 'Unknown error');
      },
    });
  }

  onRefresh() {
    this.loadAnalysisData();
  }

  goBack() {
    // If we have dimensionId, go back to dimension details
    if (this.dimensionId) {
      this.router.navigate(['/super-admin/taxonomy', this.tenantId, this.dimensionId]);
    } else {
      // Fallback to tenant main page
      this.router.navigate(['/super-admin/taxonomy', this.tenantId]);
    }
  }

  getConfidenceColor(confidence: number): string {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'danger';
  }

  getConfidenceText(confidence: number): string {
    if (confidence >= 0.9) return 'Excellent';
    if (confidence >= 0.8) return 'Very Good';
    if (confidence >= 0.7) return 'Good';
    if (confidence >= 0.6) return 'Fair';
    if (confidence >= 0.5) return 'Poor';
    if (confidence >= 0.4) return 'Very Poor';
    return 'Rejected';
  }

  getMethodColor(method: string): string {
    switch (method.toLowerCase()) {
      case 'llm_based':
      case 'llm':
        return 'primary';
      case 'centroid':
        return 'success';
      case 'hybrid':
        return 'warning';
      default:
        return 'medium';
    }
  }

  getStrategyLabel(strategy: string): string {
    const labels: { [key: string]: string } = {
      large: 'Large (LLM)',
      'small-llm': 'Small LLM',
      'small-centroid': 'Small Centroid',
      'small-hybrid': 'Small Hybrid',
    };
    return labels[strategy] || strategy;
  }

  getStrategyColor(strategy: string): string {
    const colors: { [key: string]: string } = {
      large: 'tertiary',
      'small-llm': 'primary',
      'small-centroid': 'success',
      'small-hybrid': 'warning',
    };
    return colors[strategy] || 'medium';
  }

  trackByChunk(index: number, chunk: ChunkClassificationDetail): string {
    return chunk.chunkId;
  }

  trackByCategory(
    index: number,
    category: { category: string; weight: number; chunkCount: number; avgConfidence: number },
  ): string {
    return category.category;
  }

  truncateContent(content: string, maxLength: number = 200): string {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  }

  async showFullContent(content: string) {
    const alert = await this.alertController.create({
      header: 'Full Chunk Content',
      message: content,
      buttons: ['OK'],
      cssClass: 'content-alert',
    });
    await alert.present();
  }

  getClassificationProgress(): number {
    if (!this.analysisData || this.analysisData.totalChunks === 0) return 0;
    return (this.analysisData.classifiedChunks / this.analysisData.totalChunks) * 100;
  }

  getArticleUrl(): string {
    if (!this.analysisData) return '';
    return `/dashboard/knowledge/articles/${this.analysisData.article.id}/edit`;
  }

  async viewCategoryAnalysis(categoryName: string) {
    if (!this.tenantId || !this.dimensionId) return;

    this.router.navigate([
      '/super-admin/taxonomy',
      this.tenantId,
      this.dimensionId,
      'category',
      encodeURIComponent(categoryName),
    ]);
  }
}
